import { useState } from 'react';
import { Col, Row, Space } from 'antd';
import { useParams } from 'react-router-dom';
import useInvoices from 'hooks/useInvoices';
import { InvoicesStatuses } from 'store/invoices/types';
import useSession from 'hooks/useSession';
import { useInvoiceContext } from '../InvoiceContextProvider';
import { getCurrencyData } from 'utils/common';
import { hasPermissions } from 'config/roles/hasPermissions';
import useInvoiceTotals from '../useInvoiceTotals';
import PartiallyPaidAlert from '../components/partially-paid/PartiallyPaidAlert';
import PartyBillingWarning from '../components/PartyBillingWarning';
import BillingDetailsDrawer from '../components/billing-details-drawer/BillingDetailsDrawer';
import RecipientDetailsSection from '../components/details-section/RecipientDetailsSection';
import AdditionalChargesSection from '../components/additional-charges/AdditionalChargesSection';
import InvoiceDetailsSection from '../components/details-section/InvoiceDetailsSection';
import LiabilitySection from '../components/liability/LiabilitySection';
import TotalsSection from '../components/totals/TotalsSection';

const OverviewTab = () => {
    const { userInfo } = useSession();
    const { invoiceId } = useParams();
    const { invoice, getInvoiceById } = useInvoices(invoiceId);
    const { liabilityGross, ...totals } = useInvoiceTotals(invoice?.taxLines ?? []);
    const { customer, refetchCustomer, missingBillingDetails } = useInvoiceContext();

    const [showBillingDetailsDrawer, setShowBillingDetailsDrawer] = useState(false);

    const { currencySymbol } = getCurrencyData();
    const hasUpdateInvoicePermissions = hasPermissions(userInfo, 'invoices', 'UPDATE');
    const isDraft = invoice?.status === InvoicesStatuses.DRAFT;
    const isPartiallyPaid = invoice?.status === InvoicesStatuses.PARTIALLY_PAID;

    const isReadOnly = !isDraft || !hasUpdateInvoicePermissions;
    const showBillingDetailsWarning = missingBillingDetails && isDraft;

    if (!invoice) {
        return null;
    }

    return (
        <>
            <Space style={{ width: '100%' }} size={20} direction="vertical">
                {showBillingDetailsWarning && (
                    <PartyBillingWarning onAddBillingDetails={() => setShowBillingDetailsDrawer(true)} />
                )}

                {isPartiallyPaid && (
                    <PartiallyPaidAlert
                        amountPaid={invoice.amountPaid}
                        totalAmount={invoice.totalAmount}
                        currencySymbol={currencySymbol}
                    />
                )}

                <Row gutter={20}>
                    <Col xl={12} md={24}>
                        <InvoiceDetailsSection isReadOnly={isReadOnly} />
                    </Col>
                    <Col xl={12} md={24}>
                        <RecipientDetailsSection isReadOnly={isReadOnly} />
                    </Col>
                </Row>
                <LiabilitySection taxLines={invoice?.taxLines ?? []} />
                <AdditionalChargesSection isReadOnly={isReadOnly} liabilityGross={liabilityGross} />
                <TotalsSection currencySymbol={currencySymbol} data={totals} />
            </Space>

            <BillingDetailsDrawer
                open={showBillingDetailsDrawer}
                onClose={() => setShowBillingDetailsDrawer(false)}
                onSuccess={() => {
                    setShowBillingDetailsDrawer(false);
                    refetchCustomer();
                    if (invoiceId) getInvoiceById(invoiceId);
                }}
                customer={customer}
                isThirdPartyBillingRequired={invoice.billingRoute === 'THIRD_PARTY_BILLING'}
                contactDetails={{
                    name: invoice.customer.contact.name,
                    email: invoice.customer.contact.email,
                }}
            />
        </>
    );
};

export default OverviewTab;
